import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { LicenceInfo } from '../../entities/licenceInfo/licenceInfo';
import { User } from '../../entities/user/user';
import { ApolloService } from '../apollo.service';
import { UsersService } from '../users/users.service';
import * as fromGraphQl from './graphql';

/**
 * The groups service.
 */
@Injectable()
export class PaymentsService {
  constructor(
    private apolloService: ApolloService,
    private apollo: Apollo,
    private userService: UsersService
  ) {
  }

  public getLicenceInfos(): Observable<LicenceInfo[]> {
    return this.apolloService.query<fromGraphQl.GetLicenceInfosResponse>(fromGraphQl.GET_LICENCE_INFOS_QUERY)
      .pipe(
        take(1),
        switchMap( (result) => {
          return of( LicenceInfo.createLicenceInfos(result.data.getLicenceInfos));
        })
      );
  }

  /**
   * Method checks, if a licence type is assignable to a user. A licence is assignable if:
   * - case 1: payment is not enabled:
   *  - the user does not have this licence type assigned
   * - case 2: payment is enabled:
   *  - the user does not have this licence type assigned
   *  - the licence type is assignable (licneces if this type are available) OR the user is nexato employee
   * @param licenceType 
   * @param user 
   */
  public isLicenceTypeAssignable(licenceInfo: LicenceInfo, licenceType: string, user: User, paymentEnabled: boolean): boolean {
    if (!paymentEnabled) {
      return true;
    } else {
      // the user does not have the licence type assigned, check if the user is a nexato employee
      if (this.userService.isNexatoEmployee(user)) {
        return true;
      }
      // the user is not a nexato employee, check if the licence type is assignable
      // licenceInfo === undefined -> NO_LICENCE does not have a licence info
      if(licenceInfo === undefined){
        return true;
      }
      // now we have to check, if the licence type has slots available
      // if the licence type has no slots available, licenceInfo.assignable is false
      // but we have to keep the licence assignable, if the user already has this licence type assigned
      const isLicenceTypeAssignedToUser = this.hasLicenceOfType(user, licenceType);
      if (isLicenceTypeAssignedToUser) {
        return true
      }
      return licenceInfo.assignable;
    }
  }

  public hasLicenceOfType(user: User, licenceType: string): boolean {
    if (!user.licenceType) {
      return false;
    }
    return user?.licenceType?.id === licenceType;
  }

  /**
   * updates an group
   * @param groupInput group Input
   * @param callback method
   */
  public createLicence(input: any, callback: (arg: any) => void): void {
    // this.apolloService.mutateAndExecute(
    //   fromGraphQl.CREATE_LICENCE_MUTATION,
    //   {
    //     input: [input],
    //   },
    // (response) => {
    //   if (response instanceof Error) {
    //     // Handle error
    //     console.error('Error creating licence:', response);
    //     throw response;
    //   } else {
    //     // Handle success
    //     callback(response);
    //   }
    // }
    // );
    let variables = {
      input: input,
    };

    this.apollo
    .mutate({
      mutation: fromGraphQl.CREATE_LICENCE_MUTATION,
      variables,
      errorPolicy: 'all', // Ensures errors are returned along with data
    })
    .pipe(
      catchError((error) => {
        // Handle the error and pass it to the callback
        callback({ error, data: null });
        return throwError(error);
      })
    )
    .subscribe((result) => {
      if (result.errors) {
        // If there are errors, handle them here
        callback({ error: result.errors, data: result.data });
      } else {
        // If there's no error, just return the data
        callback({ error: null, data: result.data });
      }
    });
  }

  public getInterval(interval: string) {
    switch(interval) {
      case 'month':
          return 'Monat';
      case 'year':
          return 'Jahr';
      default: return interval;
    }
  }

  public getStatus(state: any) {
    switch(state) {
      case 'TRIAL':
          return 'Trial';
      case 'NO_PAYMENT':
          return 'Kein Payment';
      case 'RUNNING':
          return 'Aktiv';
      default: return state;
    }
  }

  getCurrency(amount: any, currencyCode: any, currencyPipe: any): string {
    try {
      const formattedValue = currencyPipe.transform(+amount / 100, currencyCode?.toUpperCase(), 'symbol');

      if (!formattedValue) return '';
      // Extract the symbol and place it at the end
      const symbolMatch = formattedValue.match(/[^\d.,]+/);
      const symbol = symbolMatch ? symbolMatch[0].trim() : '';
      const amountWithoutSymbol = formattedValue.replace(symbol, '').trim();

      return amountWithoutSymbol + symbol;
    } catch (error) {
      console.error('Error getting currency symbol', error);
      return '';
    }
  }
}
